import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, CheckCircle, Clock, Instagram, Loader2, Upload } from 'lucide-react';
import { getBackendUrl } from '../../services/origin';

const API = getBackendUrl();

/* ── SOCIAL PROOF SECTION ──────────────────────────────────── */
export function SocialProofSection({ data, token, pin, brand, tc, ts, setData }) {
  const [uploading, setUploading] = useState(null); // null | 'review_google' | 'review_tripadvisor' | 'instagram'
  const [result, setResult] = useState(null);
  const cfg = data.social_proof_config || {};
  const history = data.social_proof_history || [];

  const handleUpload = async (type, platform) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { toast.error('Immagine troppo grande (max 10MB)'); return; }

      setUploading(`${type}_${platform}`);
      setResult(null);
      try {
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(file);
        });
        const res = await axios.post(`${API}/api/my/${token}/social-proof`, {
          type, platform, image_base64: base64,
        });
        setResult(res.data);
        if (res.data.status === 'approved') {
          toast.success(res.data.message);
          // Refresh data
          try {
            const fresh = await axios.get(`${API}/api/my/${token}/dashboard`, { params: { pin } });
            setData(fresh.data);
          } catch {}
        } else if (res.data.status === 'pending_review') {
          toast.info(res.data.message);
        } else {
          toast.error(res.data.message);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Errore nel caricamento');
      } finally {
        setUploading(null);
      }
    };
    input.click();
  };

  const proofCards = [];

  // Review screenshot cards (one per platform)
  (data.review_links || []).forEach(r => {
    const already = history.find(h => h.type === 'review_screenshot' && h.platform === r.platform && h.status === 'approved');
    proofCards.push({
      key: `review_${r.platform}`,
      type: 'review_screenshot',
      platform: r.platform,
      label: `Screenshot recensione ${r.name || r.platform}`,
      points: cfg.review_screenshot_bonus || 100,
      color: r.platform === 'google' ? '#4285F4' : r.platform === 'tripadvisor' ? '#00AF87' : brand.primary_color,
      icon: Camera,
      done: !!already,
      pending: history.some(h => h.type === 'review_screenshot' && h.platform === r.platform && h.status === 'pending_review'),
    });
  });

  // Instagram tag card
  if (cfg.instagram_handle) {
    const igDone = history.find(h => h.type === 'instagram_tag' && h.status === 'approved');
    const igPending = history.some(h => h.type === 'instagram_tag' && h.status === 'pending_review');
    proofCards.push({
      key: 'instagram',
      type: 'instagram_tag',
      platform: 'instagram',
      label: `Taggaci su Instagram @${cfg.instagram_handle}`,
      points: cfg.instagram_tag_bonus || 150,
      color: '#E1306C',
      icon: Instagram,
      done: !!igDone,
      pending: igPending,
    });
  }

  if (proofCards.length === 0) return null;

  return (
    <section className="mb-4" data-testid="social-proof-section">
      <h2 className="text-base font-bold mb-1" style={{ color: tc }}>Guadagna punti extra</h2>
      <p className="text-xs mb-3" style={{ color: ts }}>Carica uno screenshot come prova per ricevere punti bonus</p>
      <div className="space-y-2">
        {proofCards.map(card => {
          const Icon = card.icon;
          const isUploading = uploading === `${card.type}_${card.platform}`;
          return (
            <button
              key={card.key}
              disabled={card.done || card.pending || isUploading}
              onClick={() => handleUpload(card.type, card.platform)}
              data-testid={`social-proof-${card.key}`}
              className="w-full flex items-center gap-3 rounded-2xl p-4 border transition-all active:scale-[0.98] disabled:opacity-60"
              style={{ backgroundColor: `${tc}06`, borderColor: card.done ? `${brand.secondary_color}40` : `${tc}08` }}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${card.color}20` }}>
                {isUploading ? (
                  <Loader2 size={18} className="animate-spin" style={{ color: card.color }} />
                ) : card.done ? (
                  <CheckCircle size={18} style={{ color: brand.secondary_color || '#3ECF8E' }} />
                ) : card.pending ? (
                  <Clock size={18} style={{ color: '#F59E0B' }} />
                ) : (
                  <Icon size={18} style={{ color: card.color }} />
                )}
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-sm" style={{ color: tc }}>{card.label}</p>
                <p className="text-xs" style={{ color: card.done ? (brand.secondary_color || '#3ECF8E') : card.pending ? '#F59E0B' : ts }}>
                  {card.done ? 'Verificato!' : card.pending ? 'In attesa di verifica...' : `+${card.points} punti bonus`}
                </p>
              </div>
              {!card.done && !card.pending && !isUploading && (
                <Upload size={16} style={{ color: ts }} />
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}
